import { useAtom } from "@effect-atom/atom-react";
import { useCallback, useEffect, useState } from "react";
import { splitPositionAtom } from "../atoms/ui";

const MIN_PANEL_WIDTH = 200; // pixels

export function Splitter() {
  const [, setSplitPosition] = useAtom(splitPositionAtom);
  const [isDragging, setIsDragging] = useState(false);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const containerWidth = window.innerWidth;
      const mouseX = e.clientX;

      // Calculate percentage
      let newPosition = (mouseX / containerWidth) * 100;

      // Enforce minimum width constraints (200px on each side)
      const minPercent = (MIN_PANEL_WIDTH / containerWidth) * 100;
      const maxPercent = 100 - minPercent;

      newPosition = Math.max(minPercent, Math.min(maxPercent, newPosition));

      setSplitPosition(newPosition);
    },
    [isDragging, setSplitPosition]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Handle touch events for mobile/tablet
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isDragging || !e.touches[0]) return;

      const containerWidth = window.innerWidth;
      const touchX = e.touches[0].clientX;

      let newPosition = (touchX / containerWidth) * 100;

      const minPercent = (MIN_PANEL_WIDTH / containerWidth) * 100;
      const maxPercent = 100 - minPercent;

      newPosition = Math.max(minPercent, Math.min(maxPercent, newPosition));

      setSplitPosition(newPosition);
    },
    [isDragging, setSplitPosition]
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("touchmove", handleTouchMove);
      document.addEventListener("touchend", handleTouchEnd);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  return (
    <div
      className={`splitter ${isDragging ? "dragging" : ""}`}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      <div className="splitter-handle" />
    </div>
  );
}
